import { useMemo } from 'react';

import { IFeatureHomePageQuery, useFeatureHomePageQuery } from 'generated/sanity-graphql';
import { useAuthContext } from 'state/auth';
import { useFeaturesContext } from 'state/features';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isNative } from 'utils/environment';

export const processFeatureHomePage = (
  data: IFeatureHomePageQuery | undefined,
  isAuthenticated: boolean,
  isApp: boolean
) => {
  const featureHomePage = data?.FeatureHomePage;
  if (!featureHomePage) {
    return null;
  }

  // Keep it verbose to make things clear and to keep type safety
  if (isApp) {
    if (isAuthenticated) {
      return featureHomePage?.appAuthenticated;
    }
    return featureHomePage?.appUnauthenticated;
  }
  if (isAuthenticated) {
    return featureHomePage?.webAuthenticated;
  }
  return featureHomePage?.webUnauthenticated;
};

export const useFeatureHomePage = () => {
  const { featureHomePageId = '', featureIdsLoading } = useFeaturesContext();
  const { isAuthenticated } = useAuthContext();

  const isUserAuthenticated = isAuthenticated();

  // This Flag determines which variation of homepage is shown to the user.
  // The ID that we pass is the sanity ID for the homepage document
  const abTestingHomepageValue = useFlag(LaunchDarklyFlag.AB_TESTING_HOME_PAGE);
  const homePageId = { featureHomePageId: abTestingHomepageValue ?? featureHomePageId };

  const { data, loading } = useFeatureHomePageQuery({
    variables: homePageId,
    skip: !featureHomePageId && !abTestingHomepageValue,
  });

  const featureHomePage = useMemo(
    () => processFeatureHomePage(data, isUserAuthenticated, isNative),
    [data, isUserAuthenticated]
  );
  const featureHomePageLoading = featureIdsLoading || loading;

  return {
    featureHomePageLoading,
    featureHomePage,
    homePageId,
  };
};
